<template>
  <form
    ref="addOrEditForm"
    method="dialog"
    class="add-or-edit-digest"
    @submit="submit()"
  >
    <div class="field">
      <label for="name">Nom : *</label>
      <input
        id="name"
        v-model="formData.name"
        data-1p-ignore
        type="text"
        autofocus
        required
      />
      <span v-if="!isFormValid && !formData.name" class="error"
        >Un nom est requis</span
      >
    </div>
    <div class="field">
      <label for="dashboard">Veille : *</label>
      <input
        v-if="targetedDashboard"
        id="dashboard"
        :value="targetedDashboard.name"
        type="text"
        disabled
      />
      <SelectFilter
        v-else
        v-model="formData.dashboard_id"
        name="Choisir une veille"
        :items="dashboardsStore.dashboards"
        item-label="name"
        item-value="_id"
        mode="form-input"
      />
      <span v-if="!isFormValid && !formData.dashboard_id" class="error"
        >Une veille est requise</span
      >
    </div>
    <div class="field">
      <label for="additional-request"
        >Requête supplémentaire : <span class="tip">(optionnel)</span></label
      >
      <textarea
        id="additional-request"
        v-model="formData.additional_query"
      ></textarea>
    </div>
    <div class="field">
      <label for="service"
        >Corpus Concernés : <span class="tip">(au moins un)</span></label
      >
      <MinerveOrbite v-if="!selectableServices.length" style="margin: auto" />
      <div v-else class="services-wrapper">
        <CheckBox
          v-for="(service, index) in selectableServices"
          :key="index"
          :checked="formData.alert_services.includes(service.name)"
          @change.stop="
            () =>
              formData.alert_services.includes(service.name)
                ? removeCorpus(service.name)
                : addCorpus(service.name)
          "
        >
          {{ service.displayName }}
        </CheckBox>
      </div>
      <span
        v-if="!isFormValid && !formData.alert_services.length > 0"
        class="error"
        >Un corpus est requis</span
      >
    </div>

    <div class="field">
      <label for="frequency_days"
        >Envoyer tous les
        <span class="tip">(au moins un, hebdomadaire)</span>
      </label>
      <div class="days-wrapper">
        <ServiceTooltip
          v-for="(day, index) in selectableDays"
          :key="index"
          :text="day.displayName"
        >
          <button
            v-focus
            class="day-icon"
            :class="{ active: formData.frequency_days.includes(day.name) }"
            @click.prevent="() => toggleDay(day.name)"
          >
            {{ day.displayName[0] }}
          </button>
        </ServiceTooltip>
      </div>

      <div>
        À
        <select v-model="formData.frequency_hour" v-focus>
          <option v-for="hh in selectableHours" :key="hh" :value="Number(hh)">
            {{ hh }}
          </option>
        </select>
        h
        <select v-model.number="formData.frequency_minutes" v-focus>
          <option :value="0">00</option>
          <option :value="30">30</option>
        </select>
      </div>
      <span
        v-if="!isFormValid && !formData.frequency_days.length > 0"
        class="error"
        >Au moins un jour est requis</span
      >
    </div>

    <div class="field">
      <label for="active">Active :</label>
      <SwitchSlide
        :is-active="formData.active"
        @toggle="formData.active = !formData.active"
      />
    </div>
  </form>
</template>

<script setup>
import { ref, computed, useTemplateRef } from "vue";
import CheckBox from "@follaw-fr/components/reusable/atoms/CheckBox.vue";
import SwitchSlide from "@follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import SelectFilter from "@follaw-fr/components/reusable/atoms/SelectFilter.vue";
import { useDigestsStore } from "@follaw-fr/stores/digests";
import { useDashboardsStore } from "@follaw-fr/stores/dashboards";
import ServiceTooltip from "../atoms/ServiceTooltip.vue";
import MinerveOrbite from "@follaw-fr/components/reusable/minerve/MinerveOrbite.vue";

const store = useDigestsStore();
const dashboardsStore = useDashboardsStore();

const props = defineProps({
  targetedDigest: {
    type: Object,
    default: null,
  },
  targetedDashboard: {
    type: Object || null,
    default: null,
  },
});

const services = ref([
  { name: "event", displayName: "Événements" },
  { name: "jorf", displayName: "Jorf" },
  { name: "petition", displayName: "Pétitions" },
  { name: "news", displayName: "Actualités" },
  { name: "legislation", displayName: "Dossiers législatifs" },
  { name: "amendment", displayName: "Amendements" },
  { name: "question", displayName: "Questions" },
  { name: "compte-rendu", displayName: "Comptes-rendus" },
  { name: "social-media", displayName: "Publications" },
  { name: "articles", displayName: "Articles" },
  { name: "transcription", displayName: "Transcriptions" },
]);

const selectableServices = computed(() =>
  services.value.filter((service) =>
    store.filters.alert_services?.includes(service.name)
  )
);

const selectableDays = [
  {
    name: "monday",
    displayName: "Lundi",
  },
  {
    name: "tuesday",
    displayName: "Mardi",
  },
  {
    name: "wednesday",
    displayName: "Mercredi",
  },
  {
    name: "thursday",
    displayName: "Jeudi",
  },
  {
    name: "friday",
    displayName: "Vendredi",
  },
  {
    name: "saturday",
    displayName: "Samedi",
  },
  {
    name: "sunday",
    displayName: "Dimanche",
  },
];

const selectableHours = [
  "00",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "23",
];

// Initialize form with existing data or default values
const formData = ref({
  name: props.targetedDigest?.name ?? "",
  dashboard_id:
    props.targetedDigest?.dashboard_id ?? props.targetedDashboard?._id ?? "",
  saved_filter_id: props.targetedDigest?.saved_filter_id ?? "",
  additional_query: props.targetedDigest?.additional_query ?? "",
  alert_services: [...(props.targetedDigest?.alert_services ?? [])],
  frequency_days: [...(props.targetedDigest?.frequency_days ?? [])],
  frequency_hour: props.targetedDigest?.frequency_hour ?? 0,
  frequency_minutes: props.targetedDigest?.frequency_minutes ?? 0,
  active: props.targetedDigest?.active ?? false,
});

const isFormValid = ref(true);

const validate = () => {
  isFormValid.value =
    formData.value.name &&
    formData.value.dashboard_id &&
    formData.value.alert_services.length > 0;
  return isFormValid.value;
};

const addCorpus = (name) => {
  formData.value.alert_services.push(name);
};
const removeCorpus = (name) => {
  formData.value.alert_services = formData.value.alert_services.filter(
    (service) => service !== name
  );
};

const toggleDay = (name) => {
  if (formData.value.frequency_days.includes(name)) {
    formData.value.frequency_days = formData.value.frequency_days.filter(
      (service) => service !== name
    );
  } else {
    formData.value.frequency_days.push(name);
  }
};

const submit = () => {
  if (isFormValid.value) {
    if (props.targetedDigest) {
      store.updateDigest(props.targetedDigest._id, formData.value);
    } else {
      store.createDigest(formData.value);
    }
  }
};

const addOrEditForm = useTemplateRef("addOrEditForm");

defineExpose({
  form: addOrEditForm,
  validate,
});

if (!store.filters?.alert_services) {
  store.readDigests();
}
</script>

<style lang="scss" scoped>
.add-or-edit-digest {
  .field {
    display: flex;
    flex-direction: column;
    gap: 5px;

    margin-top: 1rem;

    .tip {
      color: var(--clr-n8);
      font-style: italic;
      font-size: 0.938em;
    }

    input[type="text"],
    textarea {
      border: 1px solid var(--clr-p2);
      padding: 4px 10px;
      border-radius: 5px;
      background-color: var(--clr-n2);
    }

    textarea {
      height: 90px;
      resize: vertical;
    }

    .services-wrapper {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      overflow-y: auto;
      gap: 4px;
    }

    .days-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      max-height: 125px;
    }
  }

  .error {
    margin-left: 10px;
    color: var(--clr-e300);
    font-style: italic;
    font-size: 0.875em;
  }
}

.day-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  background-color: var(--clr-p2);

  &.active {
    background-color: var(--clr-p5);
    color: white;
  }
}
</style>
